import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { dtrApi } from "../dtrApi";
import { dashboardMeApi } from "../workflowApi";
import usePermissions from "../../hooks/usePermissions";

/* ---------- Query keys ---------- */
export const dtrKeys = {
  all: ["dtr"],
  periods: (year) => ["dtr", "periods", year || "current"],
  weeks: (date) => ["dtr", "weeks", date],
  cutoffEmployees: (filters) => ["dtr", "cutoffEmployees", filters],
  cutoffDays: (employeeId, params) => ["dtr", "cutoffDays", employeeId, params],
  exports: (params) => ["dtr", "exports", params],
  weekly: (filters) => ["dtr", "weekly", filters],
  entries: (filters) => ["dtr", "entries", filters],
};
export const dashboardMeKey = ["dashboardMe"];

/* ---------- Periods & review ---------- */
export const useDtrPeriods = (year) =>
  useQuery({
    queryKey: dtrKeys.periods(year),
    queryFn: () => dtrApi.getPeriods(year),
    staleTime: 5 * 60 * 1000,
  });

/** Processing progress per cut-off for the HR picker (refreshed by every DTR change). */
export const useCutoffProgress = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["dtr", "progress", params],
    queryFn: () => dtrApi.getCutoffProgress(params),
    enabled,
    placeholderData: keepPreviousData,
  });

/** The weeks of the cut-off containing `date`. */
export const useDtrWeeks = (date) =>
  useQuery({
    queryKey: dtrKeys.weeks(date),
    queryFn: () => dtrApi.getWeeks(date),
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
  });

/** HR: employees of a cut-off with status and totals (days with includeDays). */
export const useCutoffEmployees = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: dtrKeys.cutoffEmployees(filters),
    queryFn: () => dtrApi.getCutoffEmployees(filters),
    placeholderData: keepPreviousData,
    enabled,
  });

/** HR: one employee's days in a cut-off (loaded when the row opens). */
export const useCutoffEmployeeDays = (
  employeeId,
  params,
  { enabled = true } = {},
) =>
  useQuery({
    queryKey: dtrKeys.cutoffDays(employeeId, params),
    queryFn: () => dtrApi.getCutoffEmployeeDays(employeeId, params),
    enabled: !!employeeId && enabled,
  });

// Approving changes lists, days, counts and the attendance views
const useDtrMutation = (fn) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      [dtrKeys.all, dashboardMeKey, ["attendance"]].forEach((queryKey) =>
        qc.invalidateQueries({ queryKey }),
      ),
  });
};
export const useApproveCutoff = () =>
  useDtrMutation((body) => dtrApi.approveCutoff(body));
export const useUnapproveCutoff = () =>
  useDtrMutation((body) => dtrApi.unapproveCutoff(body));
export const useReopenCutoff = () =>
  useDtrMutation((body) => dtrApi.reopenCutoff(body));

/* ---------- Export ---------- */
export const useDtrExports = (params) =>
  useQuery({
    queryKey: dtrKeys.exports(params),
    queryFn: () => dtrApi.getExports(params),
    placeholderData: keepPreviousData,
  });
export const useCreateDtrExport = () =>
  useDtrMutation((body) => dtrApi.createExport(body));
/** Pre-export check of a cut-off (refreshed after every DTR change). */
export const useDtrExportCheck = (body, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["dtr", "exportCheck", body],
    queryFn: () => dtrApi.checkExport(body),
    enabled: enabled && !!body,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
export const useResolveDtrExport = () =>
  useDtrMutation((body) => dtrApi.resolveExport(body));

/* ---------- Views ---------- */
export const useDtrWeekly = (filters) =>
  useQuery({
    queryKey: dtrKeys.weekly(filters),
    queryFn: () => dtrApi.getWeekly(filters),
    placeholderData: keepPreviousData,
  });
export const useDtrEntries = (filters) =>
  useQuery({
    queryKey: dtrKeys.entries(filters),
    queryFn: () => dtrApi.getEntries(filters),
    placeholderData: keepPreviousData,
  });

/* ---------- Approver dashboard: pending counts, request links ---------- */
export const useDashboardMe = (options = {}) =>
  useQuery({
    queryKey: dashboardMeKey,
    queryFn: dashboardMeApi.getMe,
    staleTime: 60 * 1000,
    retry: false,
    ...options,
  });

/** Tab badges and menu visibility for approvers. */
/**
 * Approval counts from GET /dashboard/approvals (any signed-in user, also the
 * super admin). Until that endpoint is deployed (404), fall back to the
 * `approvals` part of GET /dashboard/me, which needs a linked employee;
 * without one there are no counts.
 */
const fetchApprovals = async (employeeId, params) => {
  try {
    return await dashboardMeApi.getApprovals(params);
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
    // The old endpoint can't filter: only use it for the unfiltered counts
    if (!employeeId || params?.companyId || params?.branchId) return {};
    return (await dashboardMeApi.getMe())?.approvals || {};
  }
};

const countsOf = (a = {}, isLoading) => ({
  isApprover: !!a.isApprover,
  validationCount: a.pendingValidations ?? 0,
  overtimeCount: a.pendingOvertime ?? 0,
  correctionCount: a.pendingCorrections ?? 0,
  evaluationCount: a.pendingEvaluations ?? 0,
  leaveCount: a.pendingLeave ?? 0,
  total: a.total ?? 0,
  requests: a.requests || [],
  isLoading,
});

/**
 * The HR dashboard's Pending approvals tile: the same counts limited to the
 * dashboard's company / branch. The sidebar badges keep usePendingCounts
 * (everything), so the two can differ.
 */
export const useDashboardApprovals = (scope = {}) => {
  const { employeeId, isLoading: permsLoading } = usePermissions();
  const params = {
    companyId: scope.companyId || undefined,
    branchId: scope.branchId || undefined,
  };
  const q = useQuery({
    queryKey: [
      ...dashboardMeKey,
      "approvals",
      "scoped",
      employeeId || "none",
      params.companyId || null,
      params.branchId || null,
    ],
    queryFn: () => fetchApprovals(employeeId, params),
    staleTime: 60 * 1000,
    retry: false,
    enabled: !permsLoading,
  });
  return countsOf(q.data, q.isLoading);
};

export const usePendingCounts = (options = {}) => {
  const { employeeId, isLoading: permsLoading } = usePermissions();
  const me = useQuery({
    queryKey: [...dashboardMeKey, "approvals", employeeId || "none"],
    queryFn: () => fetchApprovals(employeeId),
    staleTime: 60 * 1000,
    retry: false,
    ...options,
    enabled: (options.enabled ?? true) && !permsLoading,
  });
  return countsOf(me.data, me.isLoading);
};
